import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* Persistent space-switcher (owners only). A disclosure dropdown following the
   portal's ThemePicker pattern (button + popover, click-outside/Escape close,
   role=menu) — the established convention for a themed, token-aware dropdown in
   this portal. Delivers the handoff's intent (an anchored switcher showing the
   current space, opening to the others with their icon + accent, plus a
   "Back to hub" affordance). It never renders for a single-space user — the
   caller only mounts it when more than one space is reachable.

   `spaces` is the reachable list (already filtered to what the rules permit);
   `current` is the current space config; `liveSummary` optionally maps a space
   key to a real one-line count (e.g. { personal: '3 due' }) for spaces that read
   Firestore. Only the space being viewed is subscribed, so at most one entry is
   live at a time and the rest fall back to their static zero copy. */
function SpaceSwitcher({ spaces, current, liveSummary }) {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;
		const onPointer = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		const onKey = (e) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('mousedown', onPointer);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onPointer);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const go = (route) => {
		setOpen(false);
		navigate(route);
	};

	// A live count when we have one, the space's calm zero copy otherwise. The
	// fallback is NOT a claim that the space is empty — an unsubscribed space
	// simply has nothing to report from here.
	const summaryLine = (space) =>
		liveSummary?.[space.key] || space.summary?.zero || '';

	return (
		<div
			className='psp-switcher'
			ref={rootRef}
			style={{ '--psp-accent': `var(--space-${current.key})` }}>
			<button
				type='button'
				className='psp-switcher__trigger'
				aria-haspopup='menu'
				aria-expanded={open}
				aria-label={`Current space: ${current.name}. Switch space`}
				onClick={() => setOpen((v) => !v)}>
				<span
					className='psp-chip'
					style={{ '--psp-chip-size': '26px', '--psp-chip-radius': '8px' }}>
					<i className={`fa-sharp-duotone fa-solid ${current.icon}`} />
				</span>
				<span className='psp-switcher__current'>{current.name}</span>
				<i className='fa-sharp fa-solid fa-chevron-down psp-switcher__caret' />
			</button>

			{open && (
				<div className='psp-switcher__menu' role='menu'>
					<div className='psp-switcher__title'>Switch space</div>
					{spaces.map((space) => {
						const isCurrent = space.key === current.key;
						return (
							<button
								key={space.key}
								type='button'
								role='menuitem'
								className={`psp-switcher__item${
									isCurrent ? ' is-current' : ''
								}`}
								style={{
									'--psp-accent': `var(--space-${space.key})`,
								}}
								onClick={() => go(space.route)}>
								<span
									className='psp-chip'
									style={{
										'--psp-chip-size': '34px',
										'--psp-chip-radius': '10px',
									}}>
									<i
										className={`fa-sharp-duotone fa-solid ${space.icon}`}
									/>
								</span>
								<span className='psp-switcher__item-body'>
									<span className='psp-switcher__item-namerow'>
										<span className='psp-switcher__item-name'>
											{space.name}
										</span>
										{isCurrent && (
											<span className='psp-pill psp-pill--accent'>
												Current
											</span>
										)}
									</span>
									<span className='psp-switcher__item-meta'>
										{summaryLine(space)}
									</span>
								</span>
								{isCurrent ? (
									<i className='fa-sharp fa-solid fa-check psp-switcher__item-end is-check' />
								) : (
									<i className='fa-sharp fa-solid fa-chevron-right psp-switcher__item-end is-chev' />
								)}
							</button>
						);
					})}

					<button
						type='button'
						role='menuitem'
						className='psp-switcher__hub'
						onClick={() => go('/portal')}>
						<i className='fa-sharp fa-solid fa-table-cells-large' />
						Back to hub
					</button>
				</div>
			)}
		</div>
	);
}

export default SpaceSwitcher;
